import { z } from 'zod'

import { FieldTypeEnum } from './custom-field.types'

/**
 * When an AI field recomputes.
 *
 * Unlike FORMULA/LOOKUP/ROLLUP, an AI field's value is STORED — an LLM call is
 * expensive and non-deterministic, so it must not re-run on every read. These
 * modes say what does trigger a run.
 */
export const AiFieldRefreshModeEnum = z.enum([
  /** Only when a user explicitly asks for it. */
  'MANUAL',
  /** Once, when the record is created. */
  'ON_CREATE',
  /** Whenever one of `sourceFields` changes. */
  'ON_SOURCE_CHANGE',
])
export type AiFieldRefreshMode = z.infer<typeof AiFieldRefreshModeEnum>

/**
 * Stored in CustomFieldDefinition.settings.ai for AI fields.
 *
 * The prompt is instruction-only; the record's data is supplied separately as
 * structured context rather than interpolated into the prompt string, so a
 * record whose text happens to look like an instruction cannot redirect the
 * model.
 */
export const AiFieldSettingsSchema = z.object({
  /** What the model should produce, e.g. "Summarise this client's current risk in one sentence." */
  prompt: z.string().min(1).max(2000),
  /**
   * Fields from the record to expose as context (column names or custom
   * fieldNames). Empty means the resolver passes a default summary of the
   * record instead of the whole thing, which keeps prompts small and cheap.
   */
  sourceFields: z.array(z.string().min(1)).max(25).default([]),
  /**
   * Display/parse type of the result. Constrained to types an LLM can
   * reliably produce; references and files are deliberately excluded.
   */
  resultFieldType: FieldTypeEnum.default('TEXT'),
  refreshMode: AiFieldRefreshModeEnum.default('MANUAL'),
  /** Options the model must choose between, when resultFieldType is DROPDOWN. */
  choices: z.array(z.string().min(1)).max(50).optional(),
})
export type AiFieldSettings = z.infer<typeof AiFieldSettingsSchema>

/** Result types an AI field may declare. */
export const AI_FIELD_RESULT_TYPES = [
  'TEXT',
  'STRING',
  'NUMBER',
  'PERCENTAGE',
  'CURRENCY',
  'BOOLEAN',
  'DATE',
  'DROPDOWN',
] as const

export type AiFieldResultType = (typeof AI_FIELD_RESULT_TYPES)[number]

export function isSupportedAiFieldResultType(
  fieldType: string,
): fieldType is AiFieldResultType {
  return (AI_FIELD_RESULT_TYPES as readonly string[]).includes(fieldType)
}

/**
 * Validates raw AI field settings and returns the normalized shape.
 *
 * Pure and framework-free so both the API's field-definition path and the
 * resolver can share one rule set without either taking a dependency on the
 * other's module. Callers wrap the thrown Error in their own HTTP exception.
 */
export function parseAiFieldSettings(raw: unknown): AiFieldSettings {
  const parsed = AiFieldSettingsSchema.safeParse(raw)

  if (!parsed.success) {
    throw new Error(
      `Invalid AI field settings: ${parsed.error.issues
        .map(issue => `${issue.path.join('.')} ${issue.message}`)
        .join('; ')}`,
    )
  }

  if (!isSupportedAiFieldResultType(parsed.data.resultFieldType)) {
    throw new Error(
      `AI fields cannot produce ${parsed.data.resultFieldType}. Supported result types: ${AI_FIELD_RESULT_TYPES.join(', ')}.`,
    )
  }

  if (
    parsed.data.resultFieldType === 'DROPDOWN' &&
    !parsed.data.choices?.length
  ) {
    throw new Error(
      'A DROPDOWN AI field needs `choices` so the model has options to pick from.',
    )
  }

  return parsed.data
}
